import { Settings } from 'lucide-react';
import { Location } from '../types';
import { BaseForecastModelWithAvailability, ForecastModel } from '@app/common/static-data/models';
import { ModelQuickSelector } from './ModelQuickSelector';
import { LocationQuickSelector } from './LocationQuickSelector';

interface TopBarProps {
  location?: Location;
  onLocationChange?: (location: Location) => void;
  favoriteLocations?: Location[];
  onOpenPanelWithLocation?: () => void;
  loading?: boolean;
  // Model props
  availableModels?: BaseForecastModelWithAvailability[];
  favoriteModels?: Set<string>;
  selectedModels?: ForecastModel[];
  onModelToggle?: (modelId: ForecastModel) => void;
  onOpenPanel?: () => void;
  onSettingsClick?: () => void;
  isPanelOpen?: boolean;
}

export function TopBar({
  location,
  onLocationChange,
  favoriteLocations = [],
  onOpenPanelWithLocation,
  loading = false,
  availableModels = [],
  favoriteModels = new Set(),
  selectedModels = [],
  onModelToggle,
  onOpenPanel,
  onSettingsClick,
  isPanelOpen = false,
}: TopBarProps) {
  // Don't render if no location data or handler
  if (!location || !onLocationChange) {
    return null;
  }

  return (
    <div className="px-4 py-2 flex-shrink-0">
      {/* Mobile Layout: Column with absolutely positioned settings button */}
      <div className="flex flex-col gap-2 sm:hidden relative">
        {/* Absolutely positioned Settings Button */}
        {!isPanelOpen && (
          <button
            onClick={onSettingsClick}
            className="absolute top-0 right-0 w-12 h-12 min-w-[3rem] min-h-[3rem] flex items-center justify-center bg-[#E34290] rounded-full shadow-lg hover:bg-pink-600 transition-colors flex-shrink-0 z-10"
            aria-label="Open Forecast Settings"
            tabIndex={0}
          >
            <Settings className="h-6 w-6 text-white" />
          </button>
        )}

        {/* Location Section */}
        <div className="space-y-1">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide px-1">
            Location
          </div>
          <div className={`w-full${isPanelOpen ? ' py-2' : ''} ${!isPanelOpen ? 'pr-14' : ''}`}>
            <LocationQuickSelector
              location={location}
              onLocationChange={onLocationChange}
              favoriteLocations={favoriteLocations}
              onOpenPanelWithLocation={onOpenPanelWithLocation || (() => {})}
              loading={loading}
            />
          </div>
        </div>

        {/* Third Row: Model Selector */}
        {availableModels.length > 0 && onModelToggle && onOpenPanel && (
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide px-1">
                Models
              </div>
            </div>
            <div className={`w-full${isPanelOpen ? ' py-2' : ''}`} style={{ overflow: 'visible' }}>
              <ModelQuickSelector
                availableModels={availableModels}
                favoriteModels={favoriteModels}
                selectedModels={selectedModels}
                onModelToggle={onModelToggle}
                onOpenPanel={onOpenPanel}
                loading={loading}
              />
            </div>
          </div>
        )}
      </div>

      {/* Desktop Layout: Row layout (original) */}
      <div className="hidden sm:flex flex-row items-center w-full min-w-0 gap-2">
        {/* Left: Model Selector */}
        <div
          className={`flex items-center gap-2 min-w-0 flex-1${isPanelOpen ? ' py-2' : ''}`}
          style={{ overflow: 'visible' }}
        >
          {availableModels.length > 0 && onModelToggle && onOpenPanel && (
            <ModelQuickSelector
              availableModels={availableModels}
              favoriteModels={favoriteModels}
              selectedModels={selectedModels}
              onModelToggle={onModelToggle}
              onOpenPanel={onOpenPanel}
              loading={loading}
            />
          )}
        </div>

        {/* Right: Location Selector and Settings Cog */}
        <div
          className="flex flex-row items-center gap-3 flex-shrink-0"
          style={{ overflow: 'visible', minWidth: '3rem' }}
        >
          <div className={`min-w-0${isPanelOpen ? ' py-2' : ''}`}>
            <LocationQuickSelector
              location={location}
              onLocationChange={onLocationChange}
              favoriteLocations={favoriteLocations}
              onOpenPanelWithLocation={onOpenPanelWithLocation || (() => {})}
              loading={loading}
            />
          </div>
          {!isPanelOpen && (
            <div className="flex-shrink-0">
              <button
                onClick={onSettingsClick}
                className="w-10 h-10 min-w-[2.5rem] min-h-[2.5rem] flex items-center justify-center bg-[#E34290] rounded-full shadow-lg hover:bg-pink-600 transition-colors flex-shrink-0 ml-2"
                aria-label="Open Forecast Settings"
                tabIndex={0}
              >
                <Settings className="h-5 w-5 text-white" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
